import PropTypes from 'prop-types';
import { useState } from 'react';
import { alpha } from '@mui/material/styles';
import { Box, Button, Chip, IconButton, ListItemIcon, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import Iconify from 'src/components/Iconify';
import PinErrorSummary, { usePinErrorsVisible } from 'src/components/leadPins/PinErrorSummary';
import PinLeadDialog from 'src/components/dialogPopUp/PinLeadDialog';
import { piiProps } from 'src/components/DemoBlur';
import { pillBadge } from 'src/styles/LeadDetailView';
import useLeadPins from 'src/hooks/useLeadPins';
import { dispatch } from 'src/redux/store';
import { findPinForLead, isOwnPin, pinLead, pinnedByName, unpinLead, updateLeadPin } from 'src/redux/slices/leadPins';

// -----------------------------------------------
const PIN_TOOLTIP = 'Pin this lead to come back to it later';
const PINNED_TOOLTIP = 'Pinned \u2014 click to edit note or unpin';

// Square icon button matching the popup's outlined "Cancel" button: 36px tall
// (24px line-height + 5px padding x2 + 1px border x2), 1px white border, theme radius.
const headerButtonSx = (pinned) => (theme) => ({
    boxSizing: 'border-box',
    width: 36,
    height: 36,
    p: 0,
    border: '1px solid #fff',
    borderRadius: `${theme.shape.borderRadius}px`,
    color: pinned ? theme.palette.primary.main : '#fff',
    backgroundColor: pinned ? '#fff' : 'transparent',
    '&:hover': { backgroundColor: pinned ? '#fff' : 'rgba(255, 255, 255, 0.12)' },
    '&.Mui-focusVisible': { outline: '2px solid #fff', outlineOffset: 2 },
});

// Amber pill sharing the lead card's pillBadge look; pushed to the row's right end.
const pillSx = (pinned) => (theme) => ({
    // Reset the native <button> font first: key order matters, so this must precede
    // the pillBadge spread or its shorthand would override pillBadge's 13px/600/20px.
    font: 'inherit',
    ...pillBadge(
        pinned ? theme.palette.warning.main : alpha(theme.palette.warning.main, 0.12),
        pinned ? '#fff' : theme.palette.warning.dark
    ),
    border: 0,
    cursor: 'pointer',
    ml: 'auto',
    '&:focus-visible': { outline: `2px solid ${theme.palette.warning.main}`, outlineOffset: 2 },
});

// "Pin lead" / "Pinned" control in the live lead-detail header. Pins are
// shared across the practice: anyone can unpin, only the pinner can edit the
// note. Rendered by LeadDetailViewBody only for non-archived leads outside
// embedded mode.

// `variant="header"` is the icon-only square button used beside Cancel on the
// green app bar (outlined white when unpinned, solid white when pinned);
// `variant="pill"` is the amber pill in the lead-detail card's pill row; `attachedError` (from buildFailure) is recorded on the pin
// when it is created from a failed popup. `leadPracticeId` guards against
// showing the control for a lead in a practice other than the current one.
export default function LeadPinControl({ leadId, leadPracticeId, variant, attachedError, onPinned }) {
    const { practiceId, pins, loaded, practiceUserId } = useLeadPins();
    const [dialogMode, setDialogMode] = useState(null); // 'create' | 'edit' | null
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [saving, setSaving] = useState(false);

    // Hook: must run before the early returns below.
    const showErrors = usePinErrorsVisible(findPinForLead(pins, leadId));

    if (!leadId || !practiceId || !loaded) return null;
    if (leadPracticeId != null && String(leadPracticeId) !== String(practiceId)) return null;

    const isHeader = variant === 'header';
    const isPill = variant === 'pill';

    const pin = findPinForLead(pins, leadId);
    const own = isOwnPin(pin, practiceUserId);

    const closeDialog = () => setDialogMode(null);

    const handleSubmit = async (reason) => {
        setSaving(true);
        const ok = dialogMode === 'edit' && pin
            ? await dispatch(updateLeadPin(practiceId, pin.id, reason))
            : await dispatch(pinLead(practiceId, Number(leadId) || leadId, reason, undefined, attachedError || undefined));
        setSaving(false);
        if (ok) {
            closeDialog();
            if (dialogMode !== 'edit' && onPinned) onPinned();
        }
    };

    const handleUnpin = async () => {
        setMenuAnchor(null);
        if (pin) await dispatch(unpinLead(practiceId, pin.id));
    };

    const dialog = (
        <PinLeadDialog
            open={Boolean(dialogMode)}
            mode={dialogMode === 'edit' ? 'edit' : 'create'}
            initialReason={dialogMode === 'edit' ? pin?.reason || '' : ''}
            loading={saving}
            onClose={closeDialog}
            onSubmit={handleSubmit}
            attachedError={attachedError}
        />
    );

    if (!pin) {
        if (isHeader || isPill) {
            return (
                <>
                    <Tooltip title={PIN_TOOLTIP} arrow>
                        {isHeader ? (
                            <IconButton
                                data-testid="lead-pin-button"
                                data-variant={variant}
                                aria-label="Pin lead"
                                onClick={() => setDialogMode('create')}
                                sx={headerButtonSx(false)}
                            >
                                <Iconify icon="mdi:pin-outline" width={18} />
                            </IconButton>
                        ) : (
                            <Box
                                component="button"
                                type="button"
                                data-testid="lead-pin-button"
                                data-variant={variant}
                                onClick={() => setDialogMode('create')}
                                sx={pillSx(false)}
                            >
                                <Iconify icon="mdi:pin-outline" width={16} height={16} />
                                Pin lead
                            </Box>
                        )}
                    </Tooltip>
                    {dialog}
                </>
            );
        }
        return (
            <>
                <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    data-testid="lead-pin-button"
                    data-variant={variant}
                    startIcon={<Iconify icon="mdi:pin-outline" width={16} />}
                    onClick={() => setDialogMode('create')}
                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                >
                    Pin lead
                </Button>
                {dialog}
            </>
        );
    }

    const label = own ? 'Pinned' : `Pinned by ${pinnedByName(pin, practiceUserId)}`;
    const compactTooltip = (
        <>
            <Typography variant="caption" component="div">{PINNED_TOOLTIP}</Typography>
            {pin.reason && (
                <Typography variant="caption" component="div" sx={{ whiteSpace: 'pre-wrap', opacity: 0.85 }} {...piiProps}>
                    {pin.reason}
                </Typography>
            )}
        </>
    );
    const defaultTooltip = pin.reason ? (
        <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }} {...piiProps}>{pin.reason}</Typography>
    ) : 'No note';
    const tooltip = isHeader || isPill ? compactTooltip : defaultTooltip;
    const openMenu = (e) => setMenuAnchor(e.currentTarget);

    let trigger;
    if (isHeader) {
        trigger = (
                <IconButton
                    data-testid="lead-pinned-chip"
                    data-variant={variant}
                    aria-label="Pinned lead options"
                    aria-haspopup="menu"
                    onClick={openMenu}
                    sx={headerButtonSx(true)}
                >
                    <Iconify icon="mdi:pin" width={18} />
                </IconButton>
        );
    } else if (isPill) {
        trigger = (
                <Box
                    component="button"
                    type="button"
                    data-testid="lead-pinned-chip"
                    data-variant={variant}
                    aria-haspopup="menu"
                    onClick={openMenu}
                    sx={pillSx(true)}
                >
                    <Iconify icon="mdi:pin" width={16} height={16} />
                    {label}
                </Box>
        );
    } else {
        trigger = (
                <Chip
                    data-testid="lead-pinned-chip"
                    data-variant={variant}
                    color="primary"
                    variant="soft"
                    size="small"
                    icon={<Iconify icon="mdi:pin" width={16} />}
                    label={label}
                    onClick={(e) => setMenuAnchor(e.currentTarget)}
                    deleteIcon={<Iconify icon="eva:chevron-down-fill" width={16} />}
                    onDelete={(e) => setMenuAnchor(e.currentTarget.closest('.MuiChip-root') || e.currentTarget)}
                    sx={{ fontWeight: 600 }}
                />
        );
    }

    return (
        <>
            <Tooltip title={tooltip} arrow>
                {trigger}
            </Tooltip>
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {showErrors && (
                    <Box sx={{ px: 1.5, pb: 0.5, maxWidth: 320 }}>
                        <PinErrorSummary pin={pin} />
                    </Box>
                )}
                {own && (
                    <MenuItem
                        onClick={() => {
                            setMenuAnchor(null);
                            setDialogMode('edit');
                        }}
                    >
                        <ListItemIcon><Iconify icon="mdi:pencil" width={18} /></ListItemIcon>
                        Edit note
                    </MenuItem>
                )}
                <MenuItem onClick={handleUnpin} sx={{ color: 'error.main' }}>
                    <ListItemIcon><Iconify icon="mdi:pin-off" width={18} sx={{ color: 'error.main' }} /></ListItemIcon>
                    Unpin
                </MenuItem>
            </Menu>
            {dialog}
        </>
    );
}

LeadPinControl.propTypes = {
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadPracticeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    variant: PropTypes.oneOf(['default', 'header', 'pill']),
    attachedError: PropTypes.shape({ message: PropTypes.string, context: PropTypes.object }),
    onPinned: PropTypes.func,
};

LeadPinControl.defaultProps = {
    leadId: null,
    leadPracticeId: null,
    variant: 'default',
    attachedError: undefined,
    onPinned: undefined,
};
